/**
 * The satchel (M3-03).
 *
 * A list of what you are carrying, and the only place in the interface where a
 * number is allowed to appear — because a count and a weight are things a
 * person genuinely knows about their own bag. Everything else obeys M2-06's
 * rule: no health bars, no damage numbers, no percentages. Freshness in
 * particular is a **word**, never a fraction, for exactly that reason: you can
 * tell moss is turning by looking at it, and you cannot tell it is at 62%.
 *
 * Pure, like `badgesFor` and `LogBuffer`: this turns a snapshot into rows and
 * the shell puts them in the DOM. An acceptance criterion about what the
 * player is shown ("it appears in inventory with weight and freshness") should
 * be checkable without a browser, and it is — see `satchel-pane.test.ts`.
 */

import { formatWeight, type Freshness, type SatchelSnapshot } from '@annwn/protocol';

/** One row, already formatted. Nothing downstream does arithmetic. */
export interface SatchelLine {
  /** Opaque identity of this exact freshness/provenance stack. */
  readonly key: string;

  readonly defId: string;

  /** "yarrow ×6" — or the unknown name, before you have identified it. */
  readonly label: string;

  readonly weight: string;

  /**
   * The freshness word, or `null` for things that keep.
   *
   * `null` rather than "fresh": bark and nuts do not go off, and printing a
   * freshness beside them would imply a clock that is not running. Absence is
   * the honest signal.
   */
  readonly freshness: Freshness | null;

  /** True once it is past its shelf life. The pane greys these out. */
  readonly spent: boolean;

  /** A carried weapon, so the detail popup can offer to take it in hand. */
  readonly equippable: boolean;

  /** Where this particular one came from (M4-05), or `null` for the ordinary. */
  readonly provenance: string | null;

  /**
   * What this character has learned the herb *does*, or `null` (M4-03).
   *
   * Server-resolved against their own knowledge, like the name: the pane prints
   * whatever sentence it is given and has no idea what it would say for anybody
   * else. This is where "knowledge accretes on the item text itself" actually
   * shows up on a screen — a row that read `yarrow ×6` last week reads
   * `yarrow ×6 — pressed into a wound it slows the bleeding` once you have knelt
   * over somebody and watched it work.
   */
  readonly note: string | null;
}

export interface SatchelView {
  readonly lines: readonly SatchelLine[];

  /** "1.4 kg", or an empty string when carrying nothing. */
  readonly weight: string;

  /** What to show instead of rows when there is nothing to show. */
  readonly empty: string | null;
}

/**
 * Turn a satchel snapshot into rows.
 *
 * The count is folded into the label rather than given a column of its own —
 * "yarrow ×6" reads as a thing you have six of, where a right-aligned `6` in a
 * column reads as a spreadsheet, and this game has spent two milestones
 * avoiding looking like a spreadsheet.
 */
export function satchelView(satchel: SatchelSnapshot): SatchelView {
  if (satchel.items.length === 0) {
    return { lines: [], weight: '', empty: 'You are carrying nothing.' };
  }

  const lines = satchel.items.map((item, index): SatchelLine => {
    return {
      key: item.key ?? `${index}:${item.defId}:${item.freshness ?? 'lasting'}`,
      defId: item.defId,
      label: item.count > 1 ? `${item.name} ×${item.count}` : item.name,
      weight: formatWeight(item.weight),
      freshness: item.freshness,
      spent: item.freshness === 'spent',
      equippable: item.equippable ?? false,
      // A spent poultice does nothing (M4-03), so the line about what it does
      // would be a promise the rules will not keep. The row still says `spent`,
      // which is the useful half.
      note: item.freshness === 'spent' ? null : item.note,
      // A keepsake's own history (M4-05) — "cut from Gwil's thigh, under the
      // whitethorn at Bryn Tywyll". Beside `note` rather than folded into it,
      // because one is what a thing *does* and the other is where it came from,
      // and the splinter in your satchel does nothing at all.
      provenance: item.provenance,
    };
  });

  return { lines, weight: formatWeight(satchel.weight), empty: null };
}

/** How each freshness word is shown. `fresh` says nothing — see below. */
const FRESHNESS_LABEL: Readonly<Record<Freshness, string>> = {
  // Deliberately blank. Everything you have just picked is fresh, so a
  // "fresh" tag beside every row would be noise on the common case and would
  // train the eye to skip the column that matters when it says "wilting".
  fresh: '',
  wilting: 'wilting',
  old: 'past its best',
  spent: 'spent',
};

export function freshnessLabel(freshness: Freshness | null): string {
  return freshness === null ? '' : FRESHNESS_LABEL[freshness];
}
